import { Link, useParams } from 'react-router-dom';

import { api } from '../lib/api';
import {
  Alert,
  Badge,
  Card,
  EnvPill,
  PageHead,
  StatusBadge,
  formatTime,
  useLoader,
} from '../components/ui';

const EVENT_KIND: Record<string, string> = {
  send: 'muted',
  delivery: 'ok',
  open: 'info',
  click: 'info',
  bounce: 'danger',
  complaint: 'danger',
  reject: 'danger',
  rendering_failure: 'danger',
  delivery_delay: 'warn',
};

export function EmailDetail() {
  const { messageId } = useParams<{ messageId: string }>();
  const { data, error, loading, reload } = useLoader(
    () => api.emails.get(messageId!),
    [messageId],
  );

  return (
    <>
      <PageHead
        title="Message"
        subtitle={messageId}
        actions={
          <Link className="btn btn-sm" to="/emails">
            Back to logs
          </Link>
        }
      />

      {error && <Alert kind="error">{error}</Alert>}
      {loading && !data && <div className="empty">Loading…</div>}

      {data && (
        <>
          {data.failure_reason && (
            <Alert kind="error">
              <strong>Failed:</strong> {data.failure_reason}
            </Alert>
          )}

          <Card title="Details">
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
                gap: 14,
              }}
            >
              <Detail label="Status">
                <StatusBadge status={data.status} />
              </Detail>
              <Detail label="Environment">
                <EnvPill environment={data.environment} />
              </Detail>
              <Detail label="Submitted via">{data.source}</Detail>
              <Detail label="From">
                <span className="mono">{data.from_address}</span>
              </Detail>
              <Detail label="To">
                <span className="mono">{data.to_address}</span>
              </Detail>
              <Detail label="Subject">{data.subject ?? '—'}</Detail>
              <Detail label="Created">{formatTime(data.created_at)}</Detail>
              <Detail label="Sent">{formatTime(data.sent_at)}</Detail>
              {data.scheduled_at && (
                <Detail label="Scheduled for">{formatTime(data.scheduled_at)}</Detail>
              )}
            </div>

            {data.tags && data.tags.length > 0 && (
              <div style={{ marginTop: 14 }}>
                <div className="tile-label">Tags</div>
                <div className="row" style={{ marginTop: 6 }}>
                  {data.tags.map((tag) => (
                    <Badge kind="muted" key={`${tag.name}:${tag.value}`}>
                      {tag.name}: {tag.value}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {data.status === 'scheduled' && (
              <div className="row" style={{ marginTop: 16 }}>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={async () => {
                    await api.emails.cancel(data.id);
                    reload();
                  }}
                >
                  Cancel this send
                </button>
                <span className="hint">Only possible while it has not left yet.</span>
              </div>
            )}
          </Card>

          <Card title={`Timeline (${data.events.length})`}>
            {data.events.length === 0 ? (
              <div className="empty">
                No events recorded yet. Delivery notifications arrive from SES
                asynchronously.
              </div>
            ) : (
              <ul className="timeline">
                {data.events.map((event) => (
                  <li key={event.id}>
                    <time>{formatTime(event.occurred_at)}</time>
                    <Badge kind={EVENT_KIND[event.event_type] ?? 'muted'}>
                      {event.event_type}
                    </Badge>
                    {event.sub_type && (
                      <span className="mono" style={{ color: 'var(--text-muted)' }}>
                        {event.sub_type}
                      </span>
                    )}
                    {event.recipient && event.recipient !== data.to_address && (
                      <span className="mono" style={{ color: 'var(--text-faint)' }}>
                        {event.recipient}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="tile-label">{label}</div>
      <div style={{ marginTop: 4 }}>{children}</div>
    </div>
  );
}
